/**
 * **Los valores parecidos a uno que se está por crear** (xcons-docs#490): el popup de Crear
 * valor los ofrece mientras se escribe, para que "gris topo" no termine como un segundo
 * "Gris Topo". Es la defensa contra duplicados de un alta que no pasa por aprobación.
 */

/** Sin mayúsculas, tildes ni espacios de más: "  Gris  Tópo" y "gris topo" son lo mismo. */
export const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Cuántas letras hay que cambiar para pasar de una palabra a la otra. */
const distancia = (a: string, b: string) => {
  const fila = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let anterior = fila[0];
    fila[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const guardado = fila[j];
      fila[j] = Math.min(
        fila[j] + 1,
        fila[j - 1] + 1,
        anterior + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      anterior = guardado;
    }
  }
  return fila[b.length];
};

/** El mismo valor escrito distinto: lo que el popup frena en vez de sugerir. */
export const esElMismo = (a: string, b: string) => normalizar(a) === normalizar(b);

/**
 * Los parecidos a `texto`, del más al menos parecido, sin el que es el mismo. Cuenta como
 * parecido el que lo contiene o está contenido en él ("gris" → "Gris topo") y el que difiere
 * en un error de tipeo: 1 letra hasta 5 letras, 2 desde ahí.
 */
export const valoresParecidos = <O extends { label: string }>(
  texto: string,
  opciones: O[],
  maximo = 5,
): O[] => {
  const buscado = normalizar(texto);
  if (buscado.length < 3) return [];
  const tolerancia = buscado.length <= 5 ? 1 : 2;
  return opciones
    .map((o) => {
      const otro = normalizar(o.label);
      if (!otro || otro === buscado) return null;
      if (otro.includes(buscado) || buscado.includes(otro)) {
        return { o, puntaje: Math.abs(otro.length - buscado.length) / 100 };
      }
      const d = distancia(buscado, otro);
      return d <= tolerancia ? { o, puntaje: d } : null;
    })
    .filter((x): x is { o: O; puntaje: number } => x !== null)
    .sort((a, b) => a.puntaje - b.puntaje)
    .slice(0, maximo)
    .map((x) => x.o);
};
